import { Button, Group, Popover, Text, Tooltip } from '@mantine/core';
import type { ReviewReactions } from '~/shared/utils/prisma/enums';
import { IconMoodSmile, IconPlus } from '@tabler/icons-react';
import { groupBy } from 'lodash-es';
import type { Session } from '~/types/session';
import { useSession } from '~/providers/SessionProvider';
import { createContext, useContext, useMemo } from 'react';

import { constants } from '~/server/common/constants';
import type { ReactionDetails } from '~/server/selectors/reaction.selector';
import { HIDDEN_REACTOR_ID } from '~/shared/constants/reaction-privacy.constants';
import type { CommentGetReactions } from '~/types/router';
import { toStringList } from '~/utils/array-helpers';

const ReactionPickerContext = createContext<{
  onEmojiClick: (reaction: ReviewReactions) => void;
  reactions: ReactionDetails[];
  disabled: boolean;
  user?: Session['user'];
}>({
  onEmojiClick: (reaction) => reaction,
  reactions: [],
  disabled: false,
});
const useReactionPickerContext = () => useContext(ReactionPickerContext);

export function ReactionPicker({ reactions, disabled = false, onSelect }: ReactionPickerProps) {
  const { data: session } = useSession();
  const currentUser = session?.user;
  const groupedReactions = useMemo(() => groupBy(reactions, 'reaction'), [reactions]);

  return (
    <ReactionPickerContext.Provider
      value={{ onEmojiClick: onSelect, user: currentUser, reactions, disabled }}
    >
      <Group gap={4} align="center">
        <Popover shadow="md" position="top-start" withArrow withinPortal>
          <Popover.Target>
            <Button variant="subtle" color="gray" radius="xl" size="compact-xs">
              <Group gap={2}>
                <IconPlus size={14} stroke={1.5} />
                <IconMoodSmile size={14} stroke={1.5} />
              </Group>
            </Button>
          </Popover.Target>
          <Popover.Dropdown p={4}>
            {session ? (
              <ReactionSelector />
            ) : (
              <Text c="dimmed" size="xs" px="xs">
                You must be logged in to react
              </Text>
            )}
          </Popover.Dropdown>
        </Popover>
        {Object.entries(groupedReactions).map(([key, value], index) => (
          <ReactionBadge key={index} reaction={key as ReviewReactions} reactions={value} />
        ))}
      </Group>
    </ReactionPickerContext.Provider>
  );
}

type ReactionPickerProps = {
  reactions: NonNullable<CommentGetReactions>;
  onSelect: (reaction: ReviewReactions) => void;
  disabled?: boolean;
};

function ReactionBadge({ reaction, reactions }: ReactionBadgeProps) {
  const { onEmojiClick, user, disabled } = useReactionPickerContext();
  const tooltip = toStringList(
    reactions.map((reaction) =>
      reaction.user.id === user?.id
        ? 'You'
        : reaction.user.username ??
          (reaction.user.id === HIDDEN_REACTOR_ID ? 'Someone' : '<deleted user>')
    )
  );
  const reacted = reactions.findIndex((reaction) => reaction.user.id === user?.id) > -1;
  const canClick = user && !disabled;

  return (
    <Tooltip label={tooltip} withArrow withinPortal maw={300} multiline>
      <Button
        radius="xl"
        variant="light"
        color={reacted ? 'blue' : 'gray'}
        onClick={canClick ? () => onEmojiClick(reaction) : undefined}
        size="compact-xs"
      >
        <Group gap={4} align="center">
          <Text inherit>{constants.availableReactions[reaction]}</Text>
          <Text inherit>{reactions.length}</Text>
        </Group>
      </Button>
    </Tooltip>
  );
}

type ReactionBadgeProps = {
  reaction: ReviewReactions;
  reactions: ReactionPickerProps['reactions'];
};

function ReactionSelector() {
  const { onEmojiClick, disabled, user } = useReactionPickerContext();
  const canClick = user && !disabled;

  return (
    <Group gap={4}>
      {Object.entries(constants.availableReactions).map(([reaction, emoji], index) => (
        <Tooltip key={index} label={reaction}>
          <Button
            size="xs"
            radius="sm"
            variant="subtle"
            onClick={canClick ? () => onEmojiClick(reaction as ReviewReactions) : undefined}
          >
            {emoji}
          </Button>
        </Tooltip>
      ))}
    </Group>
  );
}
